import { SOUND_URLS, SOUND_VOLUME } from './radarConfig';

const KEY = 'dtv_radar_sound';
const VOL_KEY = 'dtv_radar_volume';
const buffers = {};
let ctx = null;

// Built-in synth tones (used when no sound file is configured): [frequency Hz, duration s, wave]
const TONES = {
  beep: [1250, 0.09, 'square'],
  detect: [1600, 0.12, 'square'],
  button: [900, 0.05, 'square'],
  zoom: [700, 0.07, 'triangle'],
  mode: [520, 0.1, 'triangle'],
  power: [330, 0.18, 'sawtooth'],
};

export const isRadarSoundOn = () => localStorage.getItem(KEY) === 'on';
export const setRadarSound = (on) => {
  localStorage.setItem(KEY, on ? 'on' : 'off');
  window.dispatchEvent(new Event('dtv-radar-sound'));
  if (on) unlockRadarAudio();
};

export const getRadarVolume = () => {
  const v = parseFloat(localStorage.getItem(VOL_KEY));
  return Number.isFinite(v) ? Math.min(1, Math.max(0, v)) : SOUND_VOLUME;
};
export const setRadarVolume = (v) => localStorage.setItem(VOL_KEY, String(v));

const loadBuffer = (name) => {
  const url = SOUND_URLS[name];
  if (!url || buffers[name]) return;
  buffers[name] = 'loading';
  fetch(url).then((r) => r.arrayBuffer()).then((d) => ctx.decodeAudioData(d))
    .then((b) => { buffers[name] = b; }).catch(() => { delete buffers[name]; });
};

// Must run inside a tap/click so the browser allows audio
export const unlockRadarAudio = () => {
  const AC = window.AudioContext || window.webkitAudioContext;
  if (!AC) return;
  if (!ctx) ctx = new AC();
  if (ctx.state === 'suspended') ctx.resume().catch(() => {});
  Object.keys(SOUND_URLS).forEach(loadBuffer);
};

const videoPlaying = () => [...document.querySelectorAll('video')].some((v) => !v.paused && !v.muted);

// Shenron scene sounds: always play (ignore radar mute), resolve when finished or failed
const sceneBuffers = {};
let sceneSources = [];
const getSceneBuffer = (url) => {
  if (!sceneBuffers[url]) {
    sceneBuffers[url] = fetch(url).then((r) => r.arrayBuffer()).then((d) => ctx.decodeAudioData(d))
      .catch(() => { delete sceneBuffers[url]; return null; });
  }
  return sceneBuffers[url];
};
export const preloadSceneSounds = (urls) => { unlockRadarAudio(); if (ctx) urls.forEach(getSceneBuffer); };
export const stopSceneSounds = () => {
  sceneSources.forEach((s) => { try { s.stop(); } catch (e) { /* already stopped */ } });
  sceneSources = [];
};
export const playSceneSound = async (url, volume = 0.9) => {
  if (!ctx) return;
  const buf = await getSceneBuffer(url);
  if (!buf || ctx.state !== 'running') return;
  await new Promise((resolve) => {
    const src = ctx.createBufferSource();
    src.buffer = buf;
    src.connect(output(volume));
    src.onended = () => { sceneSources = sceneSources.filter((s) => s !== src); resolve(); };
    sceneSources.push(src);
    src.start();
  });
};

const output = (vol) => {
  const gain = ctx.createGain();
  gain.gain.value = vol;
  gain.connect(ctx.destination);
  return gain;
};

const synth = (freq, dur, type, vol, delay = 0, peak = 0.4) => {
  const t = ctx.currentTime + delay;
  const osc = ctx.createOscillator();
  const env = ctx.createGain();
  osc.type = type;
  osc.frequency.setValueAtTime(freq, t);
  env.gain.setValueAtTime(0.0001, t);
  env.gain.exponentialRampToValueAtTime(peak, t + 0.01);
  env.gain.exponentialRampToValueAtTime(0.0001, t + dur);
  osc.connect(env).connect(output(vol));
  osc.start(t);
  osc.stop(t + dur + 0.02);
};

// Spooky ghost wail on catch: a wobbling sine that rises then sinks away. Follows mute + volume.
export const playGhostWail = () => {
  if (!ctx || ctx.state !== 'running' || !isRadarSoundOn() || videoPlaying()) return;
  const vol = getRadarVolume();
  if (vol <= 0) return;
  const t = ctx.currentTime;
  const out = output(vol);
  [[1, 0.32], [1.5, 0.1]].forEach(([mul, peak]) => {
    const osc = ctx.createOscillator();
    const lfo = ctx.createOscillator();
    const lfoGain = ctx.createGain();
    const env = ctx.createGain();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(380 * mul, t);
    osc.frequency.exponentialRampToValueAtTime(720 * mul, t + 0.45);
    osc.frequency.exponentialRampToValueAtTime(240 * mul, t + 1.5);
    lfo.frequency.setValueAtTime(6, t);
    lfoGain.gain.setValueAtTime(22 * mul, t);
    lfo.connect(lfoGain).connect(osc.frequency);
    env.gain.setValueAtTime(0.0001, t);
    env.gain.exponentialRampToValueAtTime(peak, t + 0.15);
    env.gain.setValueAtTime(peak, t + 0.9);
    env.gain.exponentialRampToValueAtTime(0.0001, t + 1.6);
    osc.connect(env).connect(out);
    [osc, lfo].forEach((o) => { o.start(t); o.stop(t + 1.65); });
  });
};

// Victory chime on collect; a bigger flourish when all 7 are gathered. Follows mute + volume.
const CHIME = [[784, 0], [988, 0.09], [1319, 0.18]];
const FLOURISH = [[523, 0], [659, 0.1], [784, 0.2], [1047, 0.3], [784, 0.45], [1047, 0.55], [1319, 0.65], [1568, 0.8]];
export const playCollectChime = (complete = false) => {
  if (!ctx || ctx.state !== 'running' || !isRadarSoundOn() || videoPlaying()) return;
  const vol = getRadarVolume();
  if (vol <= 0) return;
  (complete ? FLOURISH : CHIME).forEach(([f, d], i, arr) => {
    const last = i === arr.length - 1;
    const dur = last ? (complete ? 1.4 : 0.6) : 0.25;
    synth(f, dur, 'triangle', vol, d, 0.35);
    synth(f * 2, dur * 0.7, 'sine', vol, d, 0.08);
  });
  if (complete) [523, 659, 784].forEach((f) => synth(f, 1.6, 'sine', vol, 0.8, 0.12));
};

// Plays only if enabled; never while a video plays. pitch speeds up / raises the sound.
export const playRadarSound = (name, pitch = 1) => {
  if (!ctx || ctx.state !== 'running' || !isRadarSoundOn() || videoPlaying()) return;
  const vol = getRadarVolume();
  if (vol <= 0) return;
  const buf = buffers[name];
  if (buf && buf !== 'loading') {
    const src = ctx.createBufferSource();
    src.buffer = buf;
    src.playbackRate.value = pitch;
    src.connect(output(vol));
    src.start();
    return;
  }
  if (SOUND_URLS[name]) { loadBuffer(name); return; }
  const tone = TONES[name];
  if (tone) synth(tone[0] * pitch, tone[1], tone[2], vol);
};
